import { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";

import { IAPIState, IFormButtons, IInfoBlockItem, IInfoBlockItemType, IStatus } from "shared/types";
import { copy } from "shared/assets";

import { ContactForm, FileForm, ImageForm, PlaceForm } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { getInfoBlockItemReadableName } from "shared/utils";

export interface CreateInfoBlockItemBaseProps {
  type: IInfoBlockItem;
  patchInfoBlock?: (item: IInfoBlockItemType, type: IInfoBlockItem) => void;
  handleClose: () => void;
  infoBlockApiStatus?: IAPIState;
  hasError?: boolean; // info block patch error
}

interface CreateInfoBlockItemHookProps extends CreateInfoBlockItemBaseProps {
  handleSubmit: (item: Partial<IInfoBlockItemType>) => void;
  createApiStatus?: IAPIState;
}

export const useCreateInfoBlockItem = ({
  type,
  handleClose,
  handleSubmit,
  createApiStatus,
  infoBlockApiStatus,
  hasError,
}: CreateInfoBlockItemHookProps) => {
  const dispatch = useDispatch();
  const [resetForm, setResetForm] = useState(false);

  const itemReadbleName = useMemo(() => getInfoBlockItemReadableName(type), [type]);

  useEffect(() => {
    if (createApiStatus === IAPIState.succeeded) {
      setResetForm(true);
      dispatch(
        addTemporaryToast({
          message: copy.createInfoBlockItem.success.replace("%s", itemReadbleName),
          status: IStatus.success,
        })
      );

      handleClose();
    }
  }, [createApiStatus, dispatch, itemReadbleName, handleClose]);

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        children: copy.createInfoBlockItem.cta.replace("%s", itemReadbleName),
        "data-testid": "create_info_block_item_confirm",
      },
      secondary: {
        onClick: handleClose,
        "data-testid": "create_info_block_item_cancel",
      },
    }),
    [handleClose, itemReadbleName]
  );

  const formProps = {
    resetForm,
    handleSubmit,
    buttons,
    hasError: hasError,
    isSubmitting: createApiStatus === IAPIState.pending || infoBlockApiStatus === IAPIState.pending,
  };

  const Form = () => {
    switch (type) {
      case IInfoBlockItem.contact:
        return ContactForm;
      case IInfoBlockItem.place:
        return PlaceForm;
      case IInfoBlockItem.file:
        return FileForm;
      case IInfoBlockItem.image:
        return ImageForm;
    }
  };

  return { formProps, Form, itemReadbleName };
};
